import { useState } from 'react';
import { Button } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { creditNoteCounterDefaultValues, CreditNoteFormValues } from '../../constants';
import { useConfirmDialogs } from '../useConfirmDialogs';
import { convertUseForFormValueToApplicableBillType } from '../../utils';
import { useCreditNoteCounterForm } from './form/useCreditNoteCounterForm';
import { CreditNoteCounterDefaultWarningDialog } from './CreditNoteCounterDefaultWarningDialog';

export interface CreditNoteCounterDialogProps {
    isEditForm: boolean;

    active: boolean;
    defaultValues: CreditNoteFormValues;
    handleClose: () => void;
    onSubmit: (values: CreditNoteFormValues) => Promise<void>;
}

export const CreditNoteCounterDialog = ({
    active,
    handleClose,
    isEditForm,
    defaultValues,
    onSubmit,
}: CreditNoteCounterDialogProps) => {
    const [isMakeDefaultConfirmationOpen, setIsMakeDefaultConfirmationOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { mutateAsync: deleteCounter } = useRepositoryMutation('counters', 'delete', {
        invalidateQueries: {
            counters: ['getAll'],
        },
    });

    const { Form, isValid, isSubmitting, hasDirtyFields, submit, watch, reset } = useCreditNoteCounterForm({
        defaultValues,
        isEditForm,
        onSubmit,
    });

    const { isDefault: isDefaultFormValue, useFor } = watch();

    const handleReset = () => {
        reset(creditNoteCounterDefaultValues, { keepDirty: true });
    };

    const onCancel = () => {
        if (hasDirtyFields) {
            setIsCloseConfirmationOpen(true);
        } else {
            close();
        }
    };

    const onSubmitAttempt = async () => {
        if (isSubmitting && !isValid) {
            return;
        }

        const showSetDefaultConfirmation =
            (isDefaultFormValue && !isEditForm) ||
            (isDefaultFormValue && isEditForm && defaultValues?.isDefault === false);

        if (showSetDefaultConfirmation) {
            setIsMakeDefaultConfirmationOpen(true);
        } else {
            await submit();
        }
    };

    const handleDelete = async () => {
        if (!defaultValues?.counterId) return;

        try {
            setLoading(true);
            await deleteCounter({
                counterId: defaultValues?.counterId,
            });
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        handleClose();
        handleReset();
    };

    const { Dialogs, setIsDeleteConfirmationOpen, setIsCloseConfirmationOpen } = useConfirmDialogs({
        closeAction: close,
        submit,
        handleDelete,
        onSubmitAttempt,
    });

    const processing = isSubmitting || loading;

    return (
        <>
            <FloatingPanel
                loading={processing}
                footer={{
                    primary: (
                        <>
                            {isEditForm ? (
                                <Button
                                    variant="secondary"
                                    size="medium"
                                    onClick={() => setIsDeleteConfirmationOpen(true)}
                                    type="submit"
                                >
                                    <LocalizedText textKey={TextKey.Delete} />
                                </Button>
                            ) : (
                                <Button variant="secondary" size="medium" onClick={() => handleReset()} type="submit">
                                    <LocalizedText textKey={TextKey.ClearForm} />
                                </Button>
                            )}
                            <Button variant="primary" size="medium" onClick={onSubmitAttempt} type="submit">
                                <LocalizedText textKey={TextKey.Save} />
                            </Button>
                        </>
                    ),
                    secondary: (
                        <Button variant="tertiary" onClick={onCancel} size="medium" type="submit">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                }}
                title={
                    isEditForm ? (
                        <LocalizedText textKey={TextKey.EditBillCounter} />
                    ) : (
                        <LocalizedText textKey={TextKey.CreditNoteCounterCreateTitle} />
                    )
                }
                active={active}
                onClose={() => {
                    handleClose();
                    handleReset();
                }}
            >
                {Form}
            </FloatingPanel>
            {Dialogs}
            <CreditNoteCounterDefaultWarningDialog
                onSubmit={async () => {
                    await submit();
                    handleClose();
                }}
                active={isMakeDefaultConfirmationOpen}
                applicableBillType={convertUseForFormValueToApplicableBillType(useFor)}
                onClose={() => {
                    setIsMakeDefaultConfirmationOpen(false);
                }}
            />
        </>
    );
};
